import { Link } from 'react-router-dom';
import type { WorkshopDto } from '@workshop-hst/shared';
import { dateParts, timeOnly, relativeFromNow } from '../lib/format';
import { Tag } from './Tag';
import { Avatar } from './Avatar';
import { GuestRow } from './GuestBadge';

export function FeaturedWorkshop({ workshop }: { workshop: WorkshopDto }) {
  const { day, month, weekday } = dateParts(workshop.startsAt);
  return (
    <Link
      to={`/w/${workshop.slug}`}
      className="group block overflow-hidden rounded-3xl border border-line bg-surface shadow-card transition hover:shadow-cardHover"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1.15fr,1fr]">
        <div className="relative aspect-[16/10] lg:aspect-auto lg:min-h-[380px] overflow-hidden">
          {workshop.coverImageUrl ? (
            <img
              src={workshop.coverImageUrl}
              alt=""
              className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.02]"
            />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-accent-soft to-canvas" />
          )}
          <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-accent backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" /> Next up
          </span>
          {workshop.tags.length > 0 ? (
            <div className="absolute bottom-4 left-4">
              <Tag>{workshop.tags[0]!}</Tag>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-5 p-7 lg:p-10">
          <div className="flex items-center gap-3 text-sm text-ink-muted">
            <div className="date-rail w-14">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">{month}</span>
              <span className="text-2xl font-bold leading-none">{day}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-medium text-ink">{weekday} · {timeOnly(workshop.startsAt)}</span>
              <span className="text-xs text-ink-subtle">{relativeFromNow(workshop.startsAt)} · {workshop.durationMin} min</span>
            </div>
          </div>

          {/* Title always rendered — accessibility, SEO, and legacy covers
              that don't have title baked in. Slight visual duplication with
              new covers is acceptable; the image is decorative, the heading
              is functional. */}
          <h2 className="text-3xl font-semibold leading-tight tracking-tight lg:text-[34px]">
            {workshop.title}
          </h2>

          {workshop.description ? (
            <p className="text-[15px] leading-relaxed text-ink-muted line-clamp-4">{workshop.description}</p>
          ) : null}

          <div className="mt-auto flex items-end justify-between gap-3 pt-2">
            <div className="flex flex-col gap-2 min-w-0">
              <div className="flex items-center gap-2.5">
                <Avatar name={workshop.hostName} url={workshop.hostHeadshotUrl} size={32} />
                <div className="flex flex-col leading-tight">
                  <span className="text-sm font-medium">{workshop.hostName}</span>
                  <span className="text-xs text-ink-subtle">Host</span>
                </div>
              </div>
              {workshop.guest ? <GuestRow guest={workshop.guest} size="lg" /> : null}
            </div>
            <span className="btn-accent shrink-0">Reserve a spot</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
